import {
  AssetMediaRef,
  PriceWording,
  productVersionLabel,
  TestedAsset,
  TestedAssetBlock,
} from "@digital-twins/shared-types";
import { studyAssetType } from "@/features/studies/questionnaire/assets/conceptAssets";

/**
 * Null-safe reads over the compiled `survey.testedAssets` block. Every study
 * report reader (authenticated and public share) sees the same survey shape,
 * so one pure module covers both call sites.
 */

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): Array<unknown> {
  return Array.isArray(value) ? value : [];
}

function isTestedAsset(value: unknown): value is TestedAsset {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    typeof (value as { id?: unknown }).id === "string"
  );
}

/**
 * Reads the tested assets off a compiled survey. Returns `[]` for a null or
 * undefined survey, a survey with no `testedAssets` block, or a block whose
 * `assets` is not an array; entries without a string `id` are dropped rather
 * than failing the whole read.
 */
export function readTestedAssets(survey: unknown): Array<TestedAsset> {
  const record = asRecord(survey);
  const assets = asArray(asRecord(record.testedAssets).assets);
  return assets.filter(isTestedAsset);
}

/**
 * Media url order, matching the builder (`ConceptAssetsPanel.tsx`):
 * `publicUrl` first, then `url`, then `filePath`.
 */
export function resolveAssetMediaUrl(media: AssetMediaRef): string | undefined {
  return media.publicUrl || media.url || media.filePath || undefined;
}

export type AssetPreviewKind = "image" | "snapshot" | "video";

export interface AssetAxisPreview {
  url: string;
  kind: AssetPreviewKind;
}

const VIDEO_FILE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

/**
 * The still a chart axis can show: an uploaded image (a captured page is
 * stored as one), then the video file itself. A video with no still still
 * counts — the axis plays a muted frame.
 */
export function resolveAssetPreview(asset: TestedAsset): AssetAxisPreview | null {
  const media = asset.media ?? [];

  const images = media.filter((item) => item.kind === "image");
  const parent = images.find((item) => !item.sliceOf) ?? images[0];
  const image = parent ? resolveAssetMediaUrl(parent) : undefined;
  if (image) {
    return { url: image, kind: "image" };
  }
  const video = media.find((item) => item.kind === "video");
  const videoUrl = video ? resolveAssetMediaUrl(video) : undefined;
  if (videoUrl) {
    return { url: videoUrl, kind: "video" };
  }
  return null;
}

/** True when the preview URL is the video file, not a poster still. */
export function assetPreviewPlays(preview: AssetAxisPreview): boolean {
  return preview.kind === "video" && VIDEO_FILE.test(preview.url);
}

/**
 * One preview per id, or null when any id has nothing to show. A mixed axis
 * (a picture beside a written name) reads as a broken column, so the chart
 * keeps text labels unless every row can be a thumbnail. Rows that show the
 * same picture keep their text labels too: price versions of one product show
 * the product's files, and three identical thumbnails hid which bar was
 * $29.99 and which $49.99 (QA4 N9); the label names each "name · price".
 */
export function previewsForAssets(
  assetIds: Array<string>,
  assets: Array<TestedAsset> | undefined,
): Array<AssetAxisPreview> | null {
  if (!assets?.length || assetIds.length === 0) {
    return null;
  }
  const byId = new Map(assets.map((asset) => [asset.id, resolveAssetPreview(asset)]));
  const previews: Array<AssetAxisPreview> = [];
  for (const assetId of assetIds) {
    const preview = byId.get(assetId);
    if (!preview) {
      return null;
    }
    previews.push(preview);
  }
  return new Set(previews.map((preview) => preview.url)).size === previews.length
    ? previews
    : null;
}

/** A tested asset with the picture the analysis resolved for it. */
export interface PreviewedAsset {
  asset: TestedAsset;
  preview: AssetAxisPreview;
}

/**
 * The report hero's slides: the tested assets the analysis has a picture for
 * (`assetPreviews`, Python's one resolver: a page's top, a video's poster), in
 * item-letter order. Empty when the analysis names none, so the hero keeps
 * the study cover.
 */
export function previewedAssets(
  assets: Array<TestedAsset>,
  itemTags: Record<string, string>,
  previews: Record<string, AssetAxisPreview> | null | undefined,
): Array<PreviewedAsset> {
  if (!previews) {
    return [];
  }
  return sortAssetsByItemTag(assets, itemTags).flatMap((asset) => {
    const preview = previews[asset.id];
    return preview ? [{ asset, preview }] : [];
  });
}

/**
 * The survey's asset context items with each priced product titled "name ·
 * price" by the one formatter (`productVersionLabel`), as the deck, the KPIs
 * and the quotes name price versions (QA4 N9), its period in the study's
 * content language (`wording`). The context title is the product's name,
 * which every version shares, so the lanes read the same name three times.
 */
export function withVersionTitles<T extends { assetId: string; title?: string }>(
  items: Array<T> | undefined,
  assets: Array<TestedAsset> | undefined,
  wording: PriceWording,
): Array<T> | undefined {
  if (!items?.length || !assets?.length) return items;
  const byId = new Map(assets.map((asset) => [asset.id, asset]));
  return items.map((item) => {
    const asset = byId.get(item.assetId);
    return asset?.type === "product" && item.title
      ? { ...item, title: productVersionLabel(item.title, asset.price, wording) }
      : item;
  });
}

/**
 * How the report names one tested asset (the dialog's rail and heading, the
 * hero slider's caption): a priced product by its one version label, "name ·
 * price" (`productVersionLabel`), so price versions of one product are told
 * apart (F26 #16); anything else by its name.
 */
export function assetLabel(asset: TestedAsset, wording: PriceWording): string {
  return productVersionLabel(
    asset.name,
    asset.type === "product" ? asset.price : undefined,
    wording,
  );
}

/** First media entry on the asset whose url resolves, if any. */
export function firstResolvableMedia(
  asset: TestedAsset,
): { media: AssetMediaRef; url: string } | undefined {
  for (const media of asset.media) {
    const url = resolveAssetMediaUrl(media);
    if (url) {
      return { media, url };
    }
  }
  return undefined;
}

/**
 * Stable sort by the letter/tag in `itemTags[asset.id]` (localeCompare).
 * Assets without a tag keep their block order, after every tagged asset.
 * Never mutates `assets`.
 */
export function sortAssetsByItemTag(
  assets: Array<TestedAsset>,
  itemTags: Record<string, string>,
): Array<TestedAsset> {
  const tagged: Array<{ asset: TestedAsset; index: number; tag: string }> = [];
  const untagged: Array<TestedAsset> = [];

  assets.forEach((asset, index) => {
    const tag = itemTags[asset.id];
    if (tag) {
      tagged.push({ asset, index, tag });
    } else {
      untagged.push(asset);
    }
  });

  tagged.sort((a, b) => a.tag.localeCompare(b.tag) || a.index - b.index);

  return [...tagged.map((entry) => entry.asset), ...untagged];
}

/**
 * The i18n key base for the asset count label: `studyReport.assetCount.ad`,
 * `studyReport.assetCount.assets`, etc. Callers append `_one`/`_other` via
 * `t(key, { count })`. Typed as the closed union of keys the locale files
 * carry, so `t` accepts it without a cast at the call site.
 */
export type AssetCountLabelKey =
  | "studyReport.assetCount.concept"
  | "studyReport.assetCount.ad"
  | "studyReport.assetCount.landing_page"
  | "studyReport.assetCount.product"
  | "studyReport.assetCount.packaging"
  | "studyReport.assetCount.message"
  | "studyReport.assetCount.brand"
  | "studyReport.assetCount.pain"
  | "studyReport.assetCount.assets";

export function assetCountLabelKey(assets: Array<TestedAsset>): AssetCountLabelKey {
  const type = studyAssetType({ assets } as TestedAssetBlock);
  return `studyReport.assetCount.${type ?? "assets"}` as AssetCountLabelKey;
}
